import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../../axiosinterceptor";

const SELECTED_RULE_ID_KEY = "selectedEditRuleId";

const initialFilters = {
  rule_id: [],
  description: [],
  pathway_entry_point: [],
  environment: [],
  status: [],
  updated_at_from: null,
  updated_at_to: null,
  page_number: 1,
  sort_by: [],
  sort_order: [],
};

const initialState = {
  cards: {
    total_rules: 0,
    active_rules: 0,
    archived_rules: 0,
    testing_rules: 0,
  },
  data: [],
  hasMore: false,
  filters: initialFilters,
  loading: false,
  error: null,

  // view_code_assist_rules (draft rule preview)
  ruleDraft: null,
  ruleDraftLoading: false,
  ruleDraftError: null,

  // get_rule_details
  ruleDetails: null,
  ruleDetailsLoading: false,
  ruleDetailsError: null,

  // move_RuleTo_Archive
  archiveLoading: false,
  archiveError: null,
  archiveResponse: null,

  selectedEditRuleId: sessionStorage.getItem(SELECTED_RULE_ID_KEY) || null,

};

export const fetchRuleDatabase = createAsyncThunk(
  "ruleDatabase/fetchRuleDatabase",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/database_rules_data", payload); // replace with your real endpoint
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch rules"
      );
    }
  }
);

// 1. view_code_assist_rules
export const viewCodeAssistRules = createAsyncThunk(
  "ruleDatabase/viewCodeAssistRules",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/view_code_assist_rules", payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch rule draft"
      );
    }
  }
);

// 2. get_rule_details
export const getRuleDetails = createAsyncThunk(
  "ruleDatabase/getRuleDetails",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/get_rule_details", payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch rule details"
      );
    }
  }
);

// 3. move_RuleTo_Archive
export const moveRuleToArchive = createAsyncThunk(
  "ruleDatabase/moveRuleToArchive",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/move_RuleTo_Archive", payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to archive rule"
      );
    }
  }
);

const RuleDatabaseSlicer = createSlice({
  name: "ruleDatabase",
  initialState,
  reducers: {
    setSearch(state, action) {
      const { type, value } = action.payload;

      // clear all searchable fields, then set only the active one
      state.filters.rule_id = [];
      state.filters.description = [];
      state.filters.pathway_entry_point = [];

      if (value) {
        if (type === "rule_id") state.filters.rule_id = [value];
        else if (type === "pathway_entry_point") state.filters.pathway_entry_point = [value];
        else state.filters.description = [value]; // default: description
      }

      state.filters.page_number = 1;
    },
    setStatusFilter(state, action) {
      state.filters.status =
        action.payload === "All Statuses" ? [] : [action.payload];
      state.filters.page_number = 1;
    },
    setEnvironmentFilter(state, action) {
      state.filters.environment = action.payload ? [action.payload] : [];
      state.filters.page_number = 1;
    },
    setDateRange(state, action) {
      state.filters.updated_at_from = action.payload.from;
      state.filters.updated_at_to = action.payload.to;
      state.filters.page_number = 1;
    },
    setPage(state, action) {
      state.filters.page_number = action.payload;
    },
    setSort(state, action) {
      const { sortBy, sortOrder } = action.payload;
      state.filters.sort_by = sortBy ? [sortBy] : [];
      state.filters.sort_order = sortOrder ? [sortOrder] : [];
    },
    resetFilters(state) {
      // state.filters = initialFilters;
     state.filters = {...initialFilters };
    },
    setSelectedEditRuleId(state, action) {
      state.selectedEditRuleId = action.payload;
      sessionStorage.setItem(SELECTED_RULE_ID_KEY, action.payload);
    },
    clearRuleDraft(state) {
      state.ruleDraft = null;
      state.ruleDraftError = null;
    },
    clearRuleDetails(state) {
      state.ruleDetails = null;
      state.ruleDetailsError = null;
    },
    clearArchiveResponse(state) {
      state.archiveResponse = null;
      state.archiveError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchRuleDatabase
      .addCase(fetchRuleDatabase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRuleDatabase.fulfilled, (state, action) => {
        state.loading = false;
        state.cards = action.payload.cards;
        state.data = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.hasMore = action.payload.has_more;
      })
      .addCase(fetchRuleDatabase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // viewCodeAssistRules
      .addCase(viewCodeAssistRules.pending, (state) => {
        state.ruleDraftLoading = true;
        state.ruleDraftError = null;
      })
      .addCase(viewCodeAssistRules.fulfilled, (state, action) => {
        state.ruleDraftLoading = false;
        state.ruleDraft = action.payload;
      })
      .addCase(viewCodeAssistRules.rejected, (state, action) => {
        state.ruleDraftLoading = false;
        state.ruleDraftError = action.payload || action.error.message;
      })

      // getRuleDetails
      .addCase(getRuleDetails.pending, (state) => {
        state.ruleDetailsLoading = true;
        state.ruleDetailsError = null;
      })
      .addCase(getRuleDetails.fulfilled, (state, action) => {
        state.ruleDetailsLoading = false;
        state.ruleDetails = action.payload;
      })
      .addCase(getRuleDetails.rejected, (state, action) => {
        state.ruleDetailsLoading = false;
        state.ruleDetailsError = action.payload || action.error.message;
      })

      // moveRuleToArchive
      .addCase(moveRuleToArchive.pending, (state) => {
        state.archiveLoading = true;
        state.archiveError = null;
      })
      .addCase(moveRuleToArchive.fulfilled, (state, action) => {
        state.archiveLoading = false;
        state.archiveResponse = action.payload;
      })
      .addCase(moveRuleToArchive.rejected, (state, action) => {
        state.archiveLoading = false;
        state.archiveError = action.payload || action.error.message;
      });
  },
});

export const {
  setSearch,
  setStatusFilter,
  setEnvironmentFilter,
  setDateRange,
  setPage,
  setSort,
  resetFilters,
  setSelectedEditRuleId,
  clearRuleDraft,
  clearRuleDetails,
  clearArchiveResponse,
  selectRuleDraft,
  selectRuleDraftLoading,
  selectRuleDraftError, 
} = RuleDatabaseSlicer.actions;

export default RuleDatabaseSlicer.reducer;